<template>
  <div class="element-pay-wrapper">
    <div id="express-checkout-element"></div>

    <a-button v-if="buttonType === CRYPTO_PAY" class="checkout-button" type="primary" @click="submit" :loading="loading">
      <a-icon v-if="!loading" type="lock" theme="filled" />
      <span>{{ orderDetails.amount }} {{ $t('message.checkout.pay') }}</span>
    </a-button>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { postCreateStripeOrder } from '@/views/checkout/api'
import { APPLE_PAY, CRYPTO_PAY, GOOGLE_PAY } from '@/enums/methods'

export default {
  props: {
    order: {
      type: Object,
      default: () => {}
    },
    buttonType: {
      type: String,
      default: ''
    },
    tipsFunction: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      CRYPTO_PAY,
      loading: false,
      orderDetails: this.$props.order,
      tipsFunc: this.$props.tipsFunction,
      orderId: this.$route.params.id
    }
  },
  methods: {
    handleError(error) {
      this.tipsFunc(error?.message || '')
    },
    async startPayment(stripe, elements, event) {
      this.loading = true
      try {
        const createInfo = event
          ? {
              channelId: this.orderDetails?.sp?.channelId,
              paymentType: event?.expressPaymentType?.replaceAll('_', '')
            }
          : { channelId: this.orderDetails?.sp?.channelId }

        // Create the PaymentIntent and obtain clientSecret
        const { clientSecret } = await postCreateStripeOrder(this.orderId, createInfo)

        const paymentInfo = elements
          ? {
              // `elements` instance used to create the Express Checkout Element
              elements,
              // `clientSecret` from the created PaymentIntent
              clientSecret,
              confirmParams: {
                return_url: `${this.orderDetails.checkoutUrl}/checkout/pending/${this.orderId}?elementType=1`
              }
            }
          : {
              clientSecret,
              confirmParams: {
                payment_method_data: {
                  type: 'crypto'
                },
                return_url: `${this.orderDetails.checkoutUrl}/checkout/pending/${this.orderId}?elementType=1`
              }
            }
        const { error } = await stripe.confirmPayment(paymentInfo)

        if (error) {
          // This point is only reached if there's an immediate error when
          // confirming the payment. Show the error to your customer (for example, payment details incomplete)
          this.handleError(error)
        } else {
          // The payment UI automatically closes with a success animation.
          // Your customer is redirected to your `return_url`.
        }
        this.loading = false
      } catch (e) {
        this.loading = false
      }
    },
    listeningStripeConfirm(stripe, expressCheckoutElement, elements) {
      // expressCheckoutElement.on('cancel', (event) => {
      //   this.handleError(event)
      // })
      expressCheckoutElement.on('confirm', async (event) => {
        const { error: submitError } = await elements.submit()
        if (submitError) {
          this.handleError(submitError)
          return
        }
        await this.startPayment(stripe, elements, event)
      })
    },
    async handleStripePayment() {
      const stripe = await loadStripe(this.orderDetails?.sp?.publicKey)
      if (!this.orderDetails.currency) {
        throw new Error('Currency not found')
      }
      const options = {
        amount: Number(this.orderDetails.sp.amount) || 0,
        currency: this.orderDetails.currency?.toLowerCase(),
        mode: 'payment',
        onBehalfOf: this.orderDetails.sp.accountId,
        // Customizable by using the Appearance API.
        appearance: {
          variables: {
            spacingUnit: '2px',
            borderRadius: '4px',
            fontSizeBase: '14px',
            fontSizeSm: '14px',
            fontSizeLg: '14px',
            fontWeightNormal: '600',
            fontWeightMedium: '600',
            fontWeightBold: '600'
          }
        }
      }
      const expressCheckoutOptions = {
        buttonHeight: 50,
        paymentMethods: {
          applePay: this.$props.buttonType === APPLE_PAY ? 'always' : 'never',
          googlePay: this.$props.buttonType === GOOGLE_PAY ? 'always' : 'never',
          link: 'never',
          paypal: 'never',
          amazonPay: 'never'
        }
        // buttonTheme: {
        //   applePay: 'white-outline'
        // }
      }
      const elements = stripe.elements(options)
      const expressCheckoutElement = elements.create('expressCheckout', expressCheckoutOptions)
      expressCheckoutElement.mount('#express-checkout-element')
      this.listeningStripeConfirm(stripe, expressCheckoutElement, elements)
    },
    async submit() {
      const stripe = await loadStripe(this.orderDetails?.sp?.publicKey)
      await this.startPayment(stripe)
    }
  },
  async mounted() {
    try {
      if (this.orderDetails?.sp && this.$props.buttonType !== CRYPTO_PAY) {
        this.handleStripePayment()
      }
    } catch (error) {
      this.handleError(error)
      console.error(error)
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/common.less';
@import '~@/styles/custom.less';

.element-pay-wrapper {
  width: 100%;
  margin-top: 4px;
}

.checkout-button {
  .pay-button();
  background-color: @checkout-hight-text-color;
}
</style>
